/**
 * Subscription Badge Component
 * Displays user's current subscription status
 */

import React from 'react'
import { CheckBadgeIcon, SparklesIcon } from '@heroicons/react/24/solid'
import { SparklesIcon as SparklesOutlineIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

interface SubscriptionBadgeProps {
  subscriptionType: string
  planName: string
  isActive: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

export const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({
  subscriptionType,
  planName,
  isActive,
  className,
  size = 'md',
}) => {
  const isFree = subscriptionType === 'free'
  const isTier1 = subscriptionType === 'student'
  const isTier2 = subscriptionType === 'researcher'

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  }

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  if (isFree) {
    return (
      <span
        className={clsx(
          'inline-flex items-center gap-1 rounded-full font-medium',
          'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
          sizeClasses[size],
          className
        )}
      >
        <SparklesOutlineIcon className={iconSizes[size]} />
        <span>Free</span>
      </span>
    )
  }

  if (isTier1) {
    return (
      <span
        className={clsx(
          'inline-flex items-center gap-1 rounded-full font-medium',
          isActive
            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
          sizeClasses[size],
          className
        )}
      >
        {isActive && <CheckBadgeIcon className={iconSizes[size]} />}
        <span>Student</span>
      </span>
    )
  }

  if (isTier2) {
    return (
      <span
        className={clsx(
          'inline-flex items-center gap-1 rounded-full font-medium',
          isActive
            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
          sizeClasses[size],
          className
        )}
      >
        {isActive && <CheckBadgeIcon className={iconSizes[size]} />}
        <span>Researcher</span>
      </span>
    )
  }

  return null
}

